import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { GridListItem } from 'react-aria-components';
import {
  SelectionCheck,
  SelectionCircle
} from 'v5/design-system/SelectionCheck';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { cn } from 'v5/platform/dom/cn';
import type { TileState } from './TileCollection';

type DataAttributes = { [name: `data-${string}`]: string | undefined };

/**
 * The one look every tile shares, around a body of the host's (`DataCard`, `ImmersiveCard`, its
 * own). A shadow on hover; on hover or the keyboard's focus its picture (the body's
 * `data-media-picture`) a touch larger and darker, the rest of the tile as it is; picked, the
 * accent ring; the black ring for the keyboard's focus only; while anything is picked, the tiles
 * not picked go grey and dim until hovered, and a hovered one's picture brightens. The circle that
 * picks sits top left, in the look of everything on a picture (`SelectionCheck` on a picture): on
 * hover or focus, always on a touch screen, and on every tile once one is picked.
 */
export function TileShell({
  tile,
  selectLabel,
  deselectLabel = selectLabel,
  check = 'overlay',
  attention = false,
  current = false,
  locked,
  slides = false,
  className,
  children,
  ...data
}: {
  tile: TileState;
  /** The circle's name. */
  selectLabel: string;
  /** Its name while picked; `selectLabel` without it. */
  deselectLabel?: string;
  /**
   * Where the circle that picks is: over the tile's top left, or the body's own
   * (`body`: it draws `SelectionCheck` where it fits, e.g. at the start of a header row).
   */
  check?: 'overlay' | 'body';
  /** Tinted, as a row the host can't fully trust (one that failed its checks). */
  attention?: boolean;
  /** The one of its set shown elsewhere (open beside the list): a muted inner ring, under the picked one. */
  current?: boolean;
  /**
   * Picked for good (already where the picks go), in the host's words: a state of its own, the
   * tile disabled (faded and pale, a not-allowed cursor) with no lift or pick look, and the grey
   * ticked circle always shown, with them as its tooltip and name. Its children marked
   * `data-tile-mark` (a chip) stay out of the fade. The host keeps the tile from being picked (`pickable`).
   */
  locked?: string;
  /** Slides to its new place when the order changes. */
  slides?: boolean;
  /** The tile's own box in the layout, e.g. its proportion. */
  className?: string;
  children: ReactNode | ((state: { isSelected: boolean }) => ReactNode);
} & DataAttributes) {
  return (
    <GridListItem
      id={tile.key}
      textValue={tile.textValue}
      onAction={tile.onAction}
      // what the selection rectangle picks
      data-item-id={tile.marquee ? tile.key : undefined}
      style={tile.style}
      data-current={current || undefined}
      data-picking={tile.picking || undefined}
      className={cn(
        '@container group/grid-item relative flex cursor-default flex-col transition-all outline-none',
        // while picking, a click on what opens the item (a picture) falls through to the tile and picks it
        '[&[data-picking]_[data-tile-opener]]:pointer-events-none',
        className
      )}
      {...data}
    >
      {({ isSelected }) => {
        const added = locked !== undefined;
        const frame = (
          <div
            // React Aria keeps `aria-current` off its row: the tile's content carries it
            aria-current={current || undefined}
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col rounded-lg transition-all duration-150 ease-out',
              '[&_[data-media-picture]]:transition-[scale,filter] [&_[data-media-picture]]:duration-150 [&_[data-media-picture]]:ease-out',
              '[&_[data-media-backdrop]]:transition-[filter] [&_[data-media-backdrop]]:duration-150 [&_[data-media-backdrop]]:ease-out',
              // already where the picks go: no lift, no pick look, and the tile but its marks
              // (`data-tile-mark`) disabled
              added
                ? [
                    'cursor-not-allowed',
                    '[&>:not([data-tile-mark])]:opacity-50 [&>:not([data-tile-mark])]:saturate-25',
                    '[&>:not([data-tile-mark])]:transition-[filter,opacity,box-shadow]'
                  ]
                : [
                    'group-hover/grid-item:shadow-sm',
                    'motion-safe:group-hocus/grid-item:[&_[data-media-picture]]:scale-102',
                    'group-data-selected/grid-item:shadow-sm group-data-selected/grid-item:ring-2 group-data-selected/grid-item:ring-border-accent-emphasis group-data-selected/grid-item:ring-offset-2'
                  ],
              'group-data-focus-visible/grid-item:ring-2! group-data-focus-visible/grid-item:ring-bg-emphasis! group-data-focus-visible/grid-item:ring-offset-2!',
              // and on what opens it from inside (its picture), which draws no ring of its own
              'has-[[data-tile-opener][data-focus-visible]]:ring-2! has-[[data-tile-opener][data-focus-visible]]:ring-bg-emphasis! has-[[data-tile-opener][data-focus-visible]]:ring-offset-2!',
              attention && 'relative bg-amber-50/50 dark:bg-amber-950/25',
              tile.picking && !added && 'cursor-pointer',
              // hovered or focused, the picture (and the blurred copy it sits on) darkens a little;
              // the rest (the data, a place without a picture) stays as it is
              !added &&
                !(tile.picking && !isSelected) && [
                  'group-hover/grid-item:[&_[data-media-picture]]:brightness-95 group-data-focus-visible/grid-item:[&_[data-media-picture]]:brightness-95',
                  'group-hover/grid-item:[&_[data-media-backdrop]]:brightness-95 group-data-focus-visible/grid-item:[&_[data-media-backdrop]]:brightness-95'
                ],
              tile.picking &&
                !isSelected &&
                !added &&
                'not-hover:brightness-80 not-hover:saturate-20',
              // while picking, hovered: the picture brightens past the dimmed neighbours; the rest of
              // the tile only drops the dimming, never past white, which would glare. Both functions
              // stay in the filter (as in the dimmed one): a different list doesn't animate
              tile.picking &&
                !isSelected &&
                !added && [
                  'group-hover/grid-item:brightness-100 group-hover/grid-item:saturate-100',
                  'group-hover/grid-item:[&_[data-media-picture]]:brightness-110'
                ]
            )}
          >
            {(tile.selectable || locked !== undefined) &&
              check === 'overlay' && (
                <div
                  data-tile-mark
                  className={cn(
                    'pointer-events-none absolute top-0 right-0 left-0 z-10 flex p-2 opacity-0 transition-opacity',
                    '*:pointer-events-auto',
                    'group-hover/grid-item:opacity-100 group-data-focus-visible/grid-item:opacity-100',
                    // and while its own menu is open or a control in it has the keyboard's focus
                    'group-hocus/grid-item:opacity-100',
                    '[@media(hover:none)]:opacity-100',
                    (tile.picking || locked !== undefined) && 'opacity-100'
                  )}
                >
                  {locked !== undefined ? (
                    <SimpleTooltip title={locked}>
                      <span
                        role="img"
                        aria-label={locked}
                        className="rounded-full"
                      >
                        <SelectionCircle onPicture locked />
                      </span>
                    </SimpleTooltip>
                  ) : (
                    <SelectionCheck
                      label={isSelected ? deselectLabel : selectLabel}
                      onPicture
                      excludeFromTabOrder
                    />
                  )}
                </div>
              )}
            {typeof children === 'function'
              ? children({ isSelected })
              : children}
          </div>
        );
        return (
          <>
            {slides ? (
              <motion.div
                layout
                layoutDependency={tile.arrangement}
                className="flex min-h-0 min-w-0 flex-1 flex-col"
              >
                {frame}
              </motion.div>
            ) : (
              frame
            )}
            {tile.dropLine}
          </>
        );
      }}
    </GridListItem>
  );
}
